import { type NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import {
    type IODDConfig,
    type IODDDataType,
    type ProcessDataItem,
    ioLinkMasterConnectionConfigSchema,
} from '@redviking/argonaut-util/types/connections/io-link-master.connection-config.zod';
import { SelectAllConnectionsDocument, type SelectAllConnectionsQuery } from 'types/db';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { gqlClient } from 'src/util/gql-client';
import type { NodeSpec } from './NodeSpec';
import { type FlowNodeControl, defineControlOpts } from '../../controls/FlowNodeControl';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

type IOLinkControlValues = NodeCfg<'ioLink'>['controlValues'];

/** a selectable device operating mode on a specific channel of an io link master connection */
type IOLinkModeOption = {
    label: string;
    value: string;
    channelNumber: number;
    conditionValue: IOLinkControlValues['conditionValue'];
    modeName: string;
    dataTypes: IODDConfig['dataTypes'];
    processData: ProcessDataItem;
};

function getModeOptionValue (channelNumber: number, conditionValue: IOLinkControlValues['conditionValue']): string {
    return `${channelNumber}:${conditionValue}`;
}

function resolveDataType (item: ProcessDataItem['processDataIn'][string], dataTypes: IODDConfig['dataTypes']): IODDDataType | undefined {
    return item.kind === 'dataRefLookup' ? dataTypes[item.dataTypeRef] : item.dataType;
}

/** object (complex) data types can't be represented as node sockets, so they are omitted */
function buildProcessDataIn (mode: IOLinkModeOption): IOLinkControlValues['processDataIn'] {
    const processDataIn: IOLinkControlValues['processDataIn'] = {};
    for (const [ id, item ] of Object.entries(mode.processData.processDataIn)) {
        const dataType = resolveDataType(item, mode.dataTypes);
        if (dataType && dataType.dataKind !== 'object') {
            processDataIn[id] = dataType.dataKind;
        }
    }
    return processDataIn;
}

function buildProcessDataOut (mode: IOLinkModeOption): IOLinkControlValues['processDataOut'] {
    const processDataOut: IOLinkControlValues['processDataOut'] = {};
    for (const [ id, item ] of Object.entries(mode.processData.processDataOut)) {
        const dataType = resolveDataType(item, mode.dataTypes);
        if (dataType && dataType.dataKind !== 'object') {
            processDataOut[id] = item.kind === 'dataRefLookup'
                ? { kind: 'dataRefLookup', dataTypeRef: item.dataTypeRef, displayName: item.displayName }
                : { kind: 'selfDefined', dataType: item.dataType, displayName: item.displayName };
        }
    }
    return processDataOut;
}

/** fetches the io link master connection config and flattens every channel's device operating modes into selectable options */
async function getModeOptions (connectionId: string): Promise<IOLinkModeOption[]> {
    if (!connectionId) {
        return [];
    }
    let result: SelectAllConnectionsQuery;
    try {
        result = await gqlClient.request({
            document: SelectAllConnectionsDocument,
            variables: { filter: { connection_id: { _eq: connectionId } } },
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch IO Link master connection config', err);
        return [];
    }
    if (!result.argo_connection_config.length) {
        // eslint-disable-next-line no-console
        console.error(`No released connection config found for connection id "${connectionId}"`);
        return [];
    }
    const connectionConfig = result.argo_connection_config[0];
    // the `config` column only stores the connection-kind-specific details; the discriminant `kind` lives on the
    // connection itself and needs to be recombined before parsing (mirrors `getConnection` in connections.entity.ts)
    const parsed = ioLinkMasterConnectionConfigSchema.safeParse({
        kind: connectionConfig.connection.kind,
        details: connectionConfig.config,
    });
    if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.error('IO Link master connection config failed to parse', parsed.error.issues, connectionConfig.config);
        return [];
    }

    const options: IOLinkModeOption[] = [];
    const ioddChannels = Object.entries(parsed.data.details.channels)
        .flatMap(([ channelNum, channelConfig ]) => (channelConfig.kind === 'iodd' && channelConfig.config
            ? [ { channelNum, config: channelConfig.config } ]
            : []));
    for (const { channelNum, config } of ioddChannels) {
        const { deviceName, processData, dataTypes } = config;
        const channelNumber = Number(channelNum);
        if (processData.kind === 'default') {
            options.push({
                label: `Ch ${channelNumber}: ${deviceName}`,
                value: getModeOptionValue(channelNumber, 'default'),
                channelNumber,
                conditionValue: 'default',
                modeName: deviceName,
                dataTypes,
                processData: processData.default,
            });
        } else {
            for (const [ optionValue, optionCfg ] of Object.entries(processData.options)) {
                const conditionValue = Number(optionValue);
                options.push({
                    label: `Ch ${channelNumber}: ${deviceName} - ${optionCfg.name}`,
                    value: getModeOptionValue(channelNumber, conditionValue),
                    channelNumber,
                    conditionValue,
                    modeName: `${deviceName} - ${optionCfg.name}`,
                    dataTypes,
                    processData: optionCfg,
                });
            }
        }
    }
    return options;
}

function getEmptyModeControlValues (): Omit<IOLinkControlValues, 'connectionId'> {
    return {
        modeName: '',
        dataTypes: {},
        channelNumber: -1,
        processDataIn: {},
        processDataOut: {},
        conditionValue: 'default',
    };
}

/** builds the control values for a node once a device operating mode has been picked (or re-synced via refresh) */
function getModeControlValues (mode: IOLinkModeOption): Omit<IOLinkControlValues, 'connectionId'> {
    return {
        modeName: mode.modeName,
        dataTypes: mode.dataTypes,
        channelNumber: mode.channelNumber,
        conditionValue: mode.conditionValue,
        processDataIn: buildProcessDataIn(mode),
        processDataOut: buildProcessDataOut(mode),
    };
}

export const ioLinkUiNodeSpec: NodeSpec<NodeCfg<'ioLink'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'IO Link Custom',
    requiredSockets: 'none',
    kindLabel: cfg => (cfg.kind === 'ioLink' && cfg.controlValues.modeName) || 'IO Link',
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            index: 0,
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.IoLinkMaster ],

            },
            on: {
                'update:value': async (connectionId, { flowEventEmitter }) => {
                    if (connectionId !== nodeCfg.controlValues.connectionId) {
                        // the previously selected device mode belongs to the old connection, so clear it out
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg: { ...nodeCfg, controlValues: { ...getEmptyModeControlValues(), connectionId } },
                        });
                        return;
                    }
                    // re-selecting the same connection (via the refresh button): re-sync the currently selected device
                    // mode against the newest connection version, in case the iodd/process data changed upstream
                    if (nodeCfg.controlValues.channelNumber < 0) {
                        return;
                    }
                    const currentModeValue = getModeOptionValue(nodeCfg.controlValues.channelNumber, nodeCfg.controlValues.conditionValue);
                    const mode = (await getModeOptions(connectionId)).find(option => option.value === currentModeValue);
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                connectionId,
                                // the previously selected mode no longer exists on the latest connection version, so clear it out
                                ...(mode ? getModeControlValues(mode) : getEmptyModeControlValues()),
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.connectionId,
        }),
        modeName: defineControlOpts({
            index: 1,
            cfg: {
                kind: 'selectWithCallback',
                label: 'Device Mode',
                selectLabel: 'Select Device Mode...',
                items: async () => (await getModeOptions(nodeCfg.controlValues.connectionId))
                    .map(({ label, value }) => ({ label, value })),
            },
            on: {
                'select': async (value, { flowEventEmitter }) => {
                    const mode = (await getModeOptions(nodeCfg.controlValues.connectionId)).find(option => option.value === value);
                    if (!mode) {
                        return;
                    }
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: { ...nodeCfg.controlValues, ...getModeControlValues(mode) },
                        },
                    });
                },
                'update:value': () => {},
            },
            value: nodeCfg.controlValues.channelNumber >= 0
                ? getModeOptionValue(nodeCfg.controlValues.channelNumber, nodeCfg.controlValues.conditionValue)
                : '',
        }),
    }),
    cfg: () => ({
        id: getUID(),
        kind: 'ioLink',
        width: 260,
        controlValues: {
            connectionId: '',
            ...getEmptyModeControlValues(),
        },
    }),
    inputs: cfg => {
        const inputs: ReturnType<NodeSpec<NodeCfg<'ioLink'>>['inputs']> = {};
        const processDataOut = Object.entries(cfg.controlValues.processDataOut);
        if (processDataOut.length) {
            inputs.writeProcessDataOut = { socket: 'event', label: 'Write Process Data Out' };
        }
        const processDataOutTypes = processDataOut.flatMap(([ processDataOutId, dataTypeDetails ]) => {
            const dataType = dataTypeDetails.kind === 'dataRefLookup'
                ? cfg.controlValues.dataTypes[dataTypeDetails.dataTypeRef]
                : dataTypeDetails.dataType;
            return dataType && dataType.dataKind !== 'object' ? [ { processDataOutId, dataTypeDetails, dataType } ] : [];
        });
        for (const { processDataOutId, dataTypeDetails, dataType } of processDataOutTypes) {
            const value = cfg.socketDefaults?.inputs?.[processDataOutId];
            const label = dataTypeDetails.displayName;

            let childControl: FlowNodeControl.WideOpts;
            if (dataType.dataKind === 'boolean') {
                childControl = defineControlOpts({ value, cfg: { kind: 'boolean', label }, on: { 'update:value': defaultControlUpdateValueHandler } });
            } else if (dataType.kind === 'dynamic') {
                childControl = dataType.dataKind === 'number'
                    ? defineControlOpts({ value, cfg: { kind: 'number', label }, on: { 'update:value': defaultControlUpdateValueHandler } })
                    : defineControlOpts({ value, cfg: { kind: 'text', label }, on: { 'update:value': defaultControlUpdateValueHandler } });
            } else {
                childControl = defineControlOpts({
                    value,
                    cfg: {
                        kind: 'selection',
                        label,
                        placeholder: label,
                        items: Object.entries(dataType.options).map(([ optionLabel, optionValue ]) => ({
                            label: optionLabel,
                            value: optionValue,
                        })),
                    },
                    on: { 'update:value': defaultControlUpdateValueHandler },
                });
            }

            inputs[processDataOutId] = {
                socket: dataType.dataKind,
                label,
                controlOpts: defineControlOpts({
                    value: null,
                    cfg: { kind: 'deletableSocket', label, childControl },
                    on: {
                        'update:value': defaultControlUpdateValueHandler,
                        'delete': (_, { flowEventEmitter }) => {
                            const newProcessDataOut = { ...cfg.controlValues.processDataOut };
                            delete newProcessDataOut[processDataOutId];
                            flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...cfg,
                                    controlValues: { ...cfg.controlValues, processDataOut: newProcessDataOut },
                                },
                                direction: 'input',
                                removalMethod: { value: processDataOutId, kind: 'uniqueIdentifier' },
                            });
                        },
                    },
                }),
            };
        }
        return inputs;
    },
    outputs: cfg => {
        const outputs: ReturnType<NodeSpec<NodeCfg<'ioLink'>>['outputs']> = {};
        const processDataIn = Object.entries(cfg.controlValues.processDataIn);
        if (processDataIn.length) {
            outputs.processDataInReceieved = { socket: 'event', label: 'Process Data In Received' };
        }
        for (const [ processDataInId, dataType ] of processDataIn) {
            outputs[processDataInId] = {
                socket: dataType,
                controlOpts: defineControlOpts({
                    value: null,
                    cfg: {
                        label: processDataInId,
                        kind: 'deletableSocket',
                        rightAlign: true,
                    },
                    on: {
                        'update:value': () => {}, // no-op, this control does not use the update:value event
                        'delete': (_, { flowEventEmitter }) => {
                            const newProcessDataIn = { ...cfg.controlValues.processDataIn };
                            delete newProcessDataIn[processDataInId];
                            flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...cfg,
                                    controlValues: { ...cfg.controlValues, processDataIn: newProcessDataIn },
                                },
                                direction: 'output',
                                removalMethod: { value: processDataInId, kind: 'uniqueIdentifier' },
                            });
                        },
                    },
                }),
            };
        }
        return outputs;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.connectionId) {
            errors.connectionId = 'Connection id is required';
        } else if (node.controlValues.channelNumber < 0) {
            errors.modeName = 'Device mode is required';
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
